import React, { useEffect, useState, useCallback } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import userService from '../services/userService';

const MIN_PASSWORD = 8;
const EMPTY = { uid: null, uname: '', uemail: '', ufull_name: '', urole: 'doctor', uis_active: true, uedit_partners: false, upasswd: '', hpcsa_number: '', bills_under: 'group', bhf_practice_number: '' };

const currentUid = () => {
  const raw = localStorage.getItem('uid');
  return raw ? parseInt(raw, 10) : null;
};

const UsersAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = form.uid !== null;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsers(await userService.list());
    } catch (err) {
      setError(err.response?.data?.err || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setFormErr(''); setModalOpen(true); };
  const openEdit = (u) => {
    setForm({ uid: u.uid, uname: u.uname, uemail: u.uemail || '', ufull_name: u.ufull_name || '', urole: u.urole, uis_active: u.uis_active, uedit_partners: !!u.uedit_partners, upasswd: '', hpcsa_number: u.hpcsa_number || '', bills_under: u.bills_under || 'group', bhf_practice_number: u.bhf_practice_number || '' });
    setFormErr('');
    setModalOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setFormErr('');

    if (!form.uname.trim()) { setFormErr('Username is required.'); return; }
    if (!isEdit && form.upasswd.length < MIN_PASSWORD) {
      setFormErr(`Password must be at least ${MIN_PASSWORD} characters.`); return;
    }
    if (isEdit && form.upasswd && form.upasswd.length < MIN_PASSWORD) {
      setFormErr(`New password must be at least ${MIN_PASSWORD} characters.`); return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await userService.update(form);
      } else {
        await userService.create(form);
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setFormErr(err.response?.data?.err || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.ufull_name || u.uname}? This cannot be undone.`)) return;
    try {
      await userService.remove(u.uid);
      await load();
    } catch (err) {
      setError(err.response?.data?.err || err.message);
    }
  };

  const meUid = currentUid();

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">User Management</h1>
          <p className="text-slate-500 mt-1">Create, edit and remove doctors and administrators for this practice.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-brand-gradient text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-95 shrink-0"
        >
          <PlusIcon className="h-5 w-5" /> New user
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Username</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No users yet.</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.uid} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-700">
                    {u.ufull_name || '—'}
                    {u.urole === 'doctor' && (
                      <div className={`text-xs font-normal ${u.hpcsa_number ? 'text-slate-400' : 'text-amber-600'}`}>
                        {u.hpcsa_number ? `HPCSA ${u.hpcsa_number}` : 'No HPCSA number'}
                        {u.bills_under === 'own' ? ` · own practice ${u.bhf_practice_number}` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.uname}</td>
                  <td className="px-4 py-3 text-slate-600">{u.uemail || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${u.urole === 'admin' ? 'bg-brand-navy/10 text-brand-navy' : 'bg-brand-teal/10 text-brand-mid'}`}>
                      {u.urole === 'admin' ? 'Administrator' : 'Doctor'}
                    </span>
                    {u.urole === 'doctor' && u.uedit_partners && (
                      <span className="ml-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700" title="Can edit partners’ schedules">
                        Edits partners
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${u.uis_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                      {u.uis_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(u)} title="Edit" className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-brand-navy">
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => remove(u)}
                        title={u.uid === meUid ? 'You cannot delete your own account' : 'Delete'}
                        disabled={u.uid === meUid}
                        className="p-2 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-brand-deep/50 backdrop-blur-sm" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-brand-deep">{isEdit ? 'Edit user' : 'New user'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={save} className="flex flex-col gap-4">
              <Field label="Full name">
                <input className={inputCls} value={form.ufull_name} onChange={(e) => setField('ufull_name', e.target.value)} placeholder="Dr. A. Jones" />
              </Field>
              <Field label="Username *">
                <input className={inputCls} value={form.uname} onChange={(e) => setField('uname', e.target.value)} placeholder="drjones" />
              </Field>
              <Field label="Email">
                <input className={inputCls} type="email" value={form.uemail} onChange={(e) => setField('uemail', e.target.value)} placeholder="dr.jones@practice.co.za" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Role">
                  <select className={inputCls} value={form.urole} onChange={(e) => setField('urole', e.target.value)}>
                    <option value="doctor">Doctor</option>
                    <option value="admin">Administrator</option>
                  </select>
                </Field>
                <Field label="Status">
                  <select className={inputCls} value={form.uis_active ? '1' : '0'} onChange={(e) => setField('uis_active', e.target.value === '1')}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </Field>
              </div>

              {form.urole === 'doctor' && (
                <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-brand-navy"
                    checked={form.uedit_partners}
                    onChange={(e) => setField('uedit_partners', e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-700">Can edit partners’ schedules</span>
                    <span className="block text-xs text-slate-500">
                      Add, move and delete sessions for doctors in the same partnership group. Everyone can always view the schedule.
                    </span>
                  </span>
                </label>
              )}

              {form.urole === 'doctor' && (
                <div className="flex flex-col gap-3 rounded-lg border border-slate-200 p-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Billing numbers</span>
                  <Field label="HPCSA registration number">
                    <input className={inputCls} value={form.hpcsa_number} onChange={(e) => setField('hpcsa_number', e.target.value.toUpperCase())} placeholder="MP0123456" />
                  </Field>
                  <Field label="Bills under">
                    <select className={inputCls} value={form.bills_under} onChange={(e) => setField('bills_under', e.target.value)}>
                      <option value="group">The practice’s BHF number (Settings)</option>
                      <option value="own">Their own BHF practice number</option>
                    </select>
                  </Field>
                  {form.bills_under === 'own' && (
                    <Field label="Own BHF practice number *">
                      <input className={inputCls} value={form.bhf_practice_number} onChange={(e) => setField('bhf_practice_number', e.target.value)} placeholder="7 digits" maxLength={9} />
                    </Field>
                  )}
                </div>
              )}

              <Field label={isEdit ? 'New password (leave blank to keep)' : 'Password *'}>
                <input className={inputCls} type="password" value={form.upasswd} onChange={(e) => setField('upasswd', e.target.value)} placeholder={`At least ${MIN_PASSWORD} characters`} />
              </Field>

              {formErr && <div className="text-red-600 text-sm">{formErr}</div>}

              <div className="flex justify-end gap-2 mt-1">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-brand-gradient hover:opacity-95 disabled:opacity-60">
                  {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const inputCls = 'p-2.5 rounded-lg border border-slate-300 text-sm outline-none w-full transition-shadow focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[0.8rem] font-semibold text-slate-600">{label}</span>
    {children}
  </label>
);

export default UsersAdmin;
